import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { UnifiedThemeProvider, createUnifiedTheme, palettes } from '@backstage/theme';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ExpandableNavigation, LightBox, ReportIssue, TextSize } from '@backstage/plugin-techdocs-module-addons-contrib';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
// thales added
import { AutoLogout } from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { RbacPage } from '@janus-idp/backstage-plugin-rbac';
import { NotificationsPage, UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { RagModal } from '@internal/backstage-plugin-rag-ai';
import { HomePageContent } from './components/home/HomePage';
import * as plugins from './plugins';
import {
  AwsIcon,
  CloudGuardIcon,
  ConfluenceIcon,
  CoverityIcon,
  DatadogIcon,
  GitLabIcon,
  JFrogIcon,
  JiraIcon,
  SendGridIcon,
  ServiceNowIcon,
  SonarQubeIcon,
  WorkdayIcon
} from './assets/customIcons';
import { ThalesDataClassification } from './lib/techdocs-addon-data-classification';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { Drawio } from './lib/techdocs-addon-drawio';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { PlaylistIndexPage, PlaylistPage } from '@backstage-community/plugin-playlist';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { SignalsDisplay } from '@backstage/plugin-signals';

const customDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    background: {
      default: '#333333',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#333333',
        },
        'a': {
          textDecoration: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1.25rem',
          fontWeight: 700,
        },
      },
    },
  },
});

const app = createApp({
  apis,
  themes: [{
    id: 'dark',
    title: 'Dark',
    variant: 'dark',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={customDarkTheme}>
        <CssBaseline />
        {children}
      </UnifiedThemeProvider>
    ),
  }],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    Object.values(plugins);
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'azure-auth-provider',
            title: 'Microsoft',
            message: 'Sign in using Microsoft',
            apiRef: microsoftAuthApiRef,
          }
        ]}
      />
    ),
  },
  icons: {
    gitlab: GitLabIcon,
    jfrog: JFrogIcon,
    sonarqube: SonarQubeIcon,
    coverity: CoverityIcon,
    jira: JiraIcon,
    servicenow: ServiceNowIcon,
    datadog: DatadogIcon,
    cloudguard: CloudGuardIcon,
    sendgrid: SendGridIcon,
    confluence: ConfluenceIcon,
    aws: AwsIcon,
    workday: WorkdayIcon,
  }
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePageContent />
    </Route>
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ThalesDataClassification />
        <ReportIssue />
        <Mermaid lightConfig={{ theme: 'default' }} darkConfig={{ theme: 'dark' }} />
        <Drawio />
        <LightBox />
        <ExpandableNavigation />
        <TextSize />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{
            'plugin:scaffolder': 'Scaffolder'
          }}
        />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/qeta" element={<QetaPage title="Questions" />} />
    <Route path="/devtools"
      element={
      <RequirePermission permission={devToolsAdministerPermission}>
        <DevToolsPage />
      </RequirePermission>
      }
    />
    <Route path="/playlist" element={<PlaylistIndexPage />} />
    <Route path="/playlist/:playlistId" element={<PlaylistPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AutoLogout />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <RagModal />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
